import { Day } from 'date-fns';
import { Frequency, RRule } from 'shared/lib/rrule';
import {
  CompletionTargetUnit,
  FrequencyUnit,
  HabitSchedule,
  RruleReferenceDate,
  ScheduleFrequency,
} from 'shared/types/habit-schedule';
import { LimitKeys } from 'shared/types/limit-keys';
import { fullWeek, WeekDays } from 'shared/types/week-days';
import { getRruleWeekDays } from 'shared/utils/get-rrule-week-days';

export { version } from '../../../package.json';
export * as FilterDocuments from './filter-document';
export * as NLP from './nlp';

export const iosDownloadLink =
  'https://apps.apple.com/us/app/griply-control-your-life/id1556692747';

export const unlimitedLimit = -1;

export const defaultLimitKeyMap: Record<LimitKeys, number> = {
  [LimitKeys.CreateGoal]: 2,
  [LimitKeys.CreateHabit]: 2,
  [LimitKeys.HabitIcons]: 0,
  [LimitKeys.HabitScheduleTarget]: 0,
  [LimitKeys.GoalUserPhoto]: 0,
  [LimitKeys.BiometricAppLock]: 0,
  [LimitKeys.CustomLifeAreas]: 0,
  [LimitKeys.CustomizeDefaultLifeAreas]: 0,
  [LimitKeys.VisionUserPhoto]: 0,
  [LimitKeys.SubGoals]: 0,
  [LimitKeys.Insights]: 0,
  [LimitKeys.MobileCalendar1DayView]: 0,
  [LimitKeys.MobileCalendar2DayView]: 0,
  [LimitKeys.MobileCalendar3DayView]: 0,
  [LimitKeys.MobileCalendarWeekView]: 0,
  [LimitKeys.DesktopCalendarDay]: 0,
  [LimitKeys.DesktopCalendarWeek]: 0,
  [LimitKeys.DesktopCalendarFourDays]: 0,
  [LimitKeys.DesktopCalendarMonth]: 0,
  [LimitKeys.DesktopGoalsOverviewTimeline]: 0,
};

export const premiumLimitKeyMap: Record<LimitKeys, number> = {
  [LimitKeys.CreateGoal]: unlimitedLimit,
  [LimitKeys.CreateHabit]: unlimitedLimit,
  [LimitKeys.HabitIcons]: unlimitedLimit,
  [LimitKeys.HabitScheduleTarget]: unlimitedLimit,
  [LimitKeys.GoalUserPhoto]: unlimitedLimit,
  [LimitKeys.BiometricAppLock]: unlimitedLimit,
  [LimitKeys.CustomLifeAreas]: unlimitedLimit,
  [LimitKeys.CustomizeDefaultLifeAreas]: unlimitedLimit,
  [LimitKeys.VisionUserPhoto]: unlimitedLimit,
  [LimitKeys.SubGoals]: unlimitedLimit,
  [LimitKeys.Insights]: unlimitedLimit,
  [LimitKeys.MobileCalendar1DayView]: unlimitedLimit,
  [LimitKeys.MobileCalendar2DayView]: unlimitedLimit,
  [LimitKeys.MobileCalendar3DayView]: unlimitedLimit,
  [LimitKeys.MobileCalendarWeekView]: unlimitedLimit,
  [LimitKeys.DesktopCalendarDay]: unlimitedLimit,
  [LimitKeys.DesktopCalendarWeek]: unlimitedLimit,
  [LimitKeys.DesktopCalendarFourDays]: unlimitedLimit,
  [LimitKeys.DesktopCalendarMonth]: unlimitedLimit,
  [LimitKeys.DesktopGoalsOverviewTimeline]: unlimitedLimit,
};

export const weekdaysNumberMap: Record<WeekDays, Day> = {
  [WeekDays.Monday]: 1,
  [WeekDays.Tuesday]: 2,
  [WeekDays.Wednesday]: 3,
  [WeekDays.Thursday]: 4,
  [WeekDays.Friday]: 5,
  [WeekDays.Saturday]: 6,
  [WeekDays.Sunday]: 0,
};

export const reminderTimes = [
  9 * 60 * 60 * 1000, // 09:00
  12 * 60 * 60 * 1000, // 12:00
  15 * 60 * 60 * 1000, // 15:00
  18 * 60 * 60 * 1000, // 18:00
  21 * 60 * 60 * 1000, // 21:00
];

export const sideNavigationHoverClass = 'side-navigation-show-on-hover';

export const whatsappLink = 'https://chat.whatsapp.com/GSri97x51ubEMEIwccPICr';
export const shortcutsLink =
  'https://griply.app/help/features/keyboard-shortcuts';
export const redditLink = 'https://www.reddit.com/r/GriplyApp/';
export const changelogLink = 'https://griply.app/changelog';

export const subTaskLevel = 1;

export const PredefinedColorOptions = [
  {
    label: 'scarlet-flame',
    value: '#FA2835',
  },
  {
    label: 'strawberry-sorbet',
    value: '#FF6E77',
  },
  {
    label: 'blush-pink',
    value: '#FFA8AD',
  },
  {
    label: 'citrus-ember',
    value: '#FF6B1C',
  },
  {
    label: 'sunset-coral',
    value: '#FBA575',
  },
  {
    label: 'toasted-peach',
    value: '#F3BD96',
  },
  {
    label: 'golden-marigold',
    value: '#F7B10C',
  },
  {
    label: 'sunshine-gold',
    value: '#FBCA75',
  },
  {
    label: 'honey-glow',
    value: '#F3E396',
  },
  {
    label: 'verdant-leaf',
    value: '#57AC40',
  },
  {
    label: 'herbal-sage',
    value: '#98BD82',
  },
  {
    label: 'frosted-mint',
    value: '#C7E0C3',
  },
  {
    label: 'ocean-teal',
    value: '#07C49B',
  },
  {
    label: 'seafoam-mist',
    value: '#86C2B6',
  },
  {
    label: 'mint-whisper',
    value: '#ACE1D6',
  },
  {
    label: 'electric-sapphire',
    value: '#066DFE',
  },
  {
    label: 'azure-wave',
    value: '#599EFF',
  },
  {
    label: 'cerulean-breeze',
    value: '#9DC6FF',
  },
  {
    label: 'royal-amethyst',
    value: '#671DD3',
  },
  {
    label: 'vivid-periwinkle',
    value: '#8875FB',
  },
  {
    label: 'cool-sky',
    value: '#96AAF3',
  },
  {
    label: 'fuchsia-rose',
    value: '#FF236F',
  },
  {
    label: 'cotton-candy',
    value: '#FF98B7',
  },
  {
    label: 'petal-pink',
    value: '#F2C5D7',
  },
  {
    label: 'midnight-indigo',
    value: '#2C2E69',
  },
  {
    label: 'dusky-lavender',
    value: '#6A6693',
  },
  {
    label: 'lavender-mist',
    value: '#B5B2CF',
  },
  {
    label: 'cinnamon-oak',
    value: '#866E53',
  },
  {
    label: 'autumn-taupe',
    value: '#A1907E',
  },
  {
    label: 'vintage-sand',
    value: '#C2B59D',
  },
  {
    label: 'deep-charcoal',
    value: '#2C2A24',
  },
  {
    label: 'steel-gray',
    value: '#8A8A8A',
  },
  {
    label: 'silvery-cloud',
    value: '#C7C7C7',
  },
];

export const durationOptions = [
  900000, // 00:15 (15 minutes)
  1800000, // 00:30 (30 minutes)
  2700000, // 00:45 (45 minutes)
  3600000, // 01:00 (1 hour)
  5400000, // 01:30 (1.5 hours)
  7200000, // 02:00 (2 hours)
  10800000, // 03:00 (3 hours)
  14400000, // 04:00 (4 hours)
];

export const defaultTagColorOption =
  PredefinedColorOptions[PredefinedColorOptions.length - 1];

export const defaultRruleOptions = {
  freq: Frequency.WEEKLY,
  byweekday: getRruleWeekDays(fullWeek),
};
export const defaultRrule = new RRule(defaultRruleOptions).toString();
export const defaultFrequency: ScheduleFrequency = {
  count: 1,
  unit: FrequencyUnit.Week,
  label: null,
};

export const createDefaultHabitSchedule: () => HabitSchedule = (
  startDate = new Date(),
) => ({
  frequency: null,
  rrule: { format: defaultRrule, referenceDate: RruleReferenceDate.StartDate },
  completionTarget: {
    count: 1,
    unit: CompletionTargetUnit.Day,
  },
  startDate,
  entries: [],
});

export const halfHour = 30 * 60 * 1000;

export const newTrialAllowedDate = new Date('2024-06-10T00:00:00Z');
